import { useState } from 'react';
import { useChangeRequests, useReviewChangeRequest, useApproveChangeRequest, useRejectChangeRequest, useApplyChangeRequest } from '../../hooks/useAdminApi';
import { ChangeRequest } from '../../api/admin-api';
import { useAuthContext } from '../../contexts/AuthContextProvider';
import { useAdminAccess } from '../../hooks/useAdminAccess';
import { useClientConfigs } from '../../contexts/ClientConfigContext';
import { PreviewPanel, bulkSetupCounts, isBulkChangeRequest, readBulkSetup, toNumber } from './ChangeRequestPreview';

type Tab = 'pending' | 'reviewed' | 'approved' | 'rejected' | 'applied';

const isBulk = isBulkChangeRequest;

function payloadEntries(changeData: Record<string, unknown>): [string, unknown][] {
    return Object.entries(changeData ?? {}).filter(([k]) => k !== 'TargetRecordIds' && k !== 'BulkSetup');
}

const FIELD_LABELS: Record<string, string> = {
    Weight:           'Appetite Weight',
    CarrierId:        'Carrier ID',
    Carrier:          'Carrier',
    KeywordId:        'Keyword ID',
    Keyword:          'Keyword',
    LineOfBusiness:   'Line of Business',
    UsStateCode:      'State',
    BusinessClass:    'Business Class',
    Value:            'Business Class Label',
    Description:      'Business Class Label',
    IsCarrierActive:  'Carrier Active',
};

const fieldLabel = (key: string) => FIELD_LABELS[key] ?? key;

const TABLE_LABELS: Record<string, string> = {
    CarrierRecommendation:      'Classic Appetite',
    CarrierAppetiteByUkw:       'Universal Appetite',
    CarrierBusinessClass:       'Business Class (Classic)',
    CarrierBusinessClassByUkw:  'Business Class (Universal)',
};
const friendlyTable = (t: string) => TABLE_LABELS[t] ?? t;

function applyTargetText(cr: ChangeRequest) {
    const setup = readBulkSetup(cr.changeData);
    if (setup) {
        const counts = bulkSetupCounts(setup);
        const carrier = setup.carrierName ?? `carrier ${setup.carrierId ?? 'unknown'}`;
        const lob = setup.lineOfBusiness ?? `line of business ${setup.lineOfBusinessId ?? 'unknown'}`;
        return `${counts.rows} rows will be written for ${carrier} / ${lob} (${counts.active} active, ${counts.inactive} inactive).`;
    }
    if (isBulk(cr)) return `${cr.targetRecordIds.length} records will be updated.`;
    if (cr.targetRecordId != null) return `Record #${cr.targetRecordId} will be updated.`;
    return 'The change will be written.';
}

const formatDate = (val: string | null | undefined) => {
    if (!val) return '—';
    const d = new Date(val);
    if (isNaN(d.getTime()) || d.getFullYear() < 2) return '—';
    return d.toLocaleString();
};

function ChangesSummary({ cr }: { cr: ChangeRequest }) {
    const setup = readBulkSetup(cr.changeData);
    if (setup) {
        const counts = bulkSetupCounts(setup);
        const states = (setup.states ?? []).join(', ');
        return (
            <span>
                {counts.rows} rows{states ? ` · ${states}` : ''} · {counts.active} active, {counts.inactive} inactive
            </span>
        );
    }

    const bulk = isBulk(cr);
    const entries = payloadEntries(cr.changeData ?? {});

    const formatVal = (k: string, v: unknown) => {
        if (k === 'Weight') {
            const n = toNumber(v);
            return n === 1 ? 'Active' : n === 0 ? 'Inactive' : String(v);
        }
        return String(v);
    };

    if (bulk) {
        const count = cr.targetRecordIds.length;
        const valueStr = entries.map(([k, v]) => `${fieldLabel(k)}: ${formatVal(k, v)}`).join(', ');
        return <span>{count} records · {valueStr}</span>;
    }

    if (entries.length === 0) return <span className="admin-muted">No field changes recorded.</span>;

    return <span>{entries.map(([k, v]) => `${fieldLabel(k)}: ${formatVal(k, v)}`).join(' · ')}</span>;
}

const TAB_CONFIG: { key: Tab; label: string; emptyMsg: string; tagClass: string }[] = [
    { key: 'pending',  label: 'Awaiting Review',   emptyMsg: 'No change requests are awaiting review.',   tagClass: 'admin-tag-attention' },
    { key: 'reviewed', label: 'Awaiting Approval', emptyMsg: 'No change requests are awaiting approval.', tagClass: 'admin-tag-tip' },
    { key: 'approved', label: 'Ready to Apply',    emptyMsg: 'No approved requests ready to apply.',      tagClass: '' },
    { key: 'rejected', label: 'Rejected',          emptyMsg: 'No rejected change requests.',              tagClass: 'admin-tag-concerning' },
    { key: 'applied',  label: 'Applied',           emptyMsg: 'No applied changes yet.',                   tagClass: 'admin-tag-confirmation' },
];

function applyEnvironmentWarning(environmentName: string | undefined) {
    const name = environmentName?.trim();
    if (!name) {
        return "The environment name could not be loaded. This will write the change to this deployment's database.";
    }
    if (name.toLowerCase() === 'pd') {
        return 'This will write the change to the pd (production) database.';
    }
    return `This will write the change to the ${name} environment database.`;
}

const SodNote = ({ text, title }: { text: string; title: string }) => (
    <span className="admin-sod-note" title={title}>
        <span className="admin-icon icon-alert" aria-hidden="true" />
        {text}
    </span>
);

const extractActionError = (e: any, fallback: string): string =>
    e?.response?.data?.message
    ?? e?.response?.data?.[0]?.message
    ?? e?.message
    ?? fallback;

export const ApprovalQueue = () => {
    const { getUserName, getUserAccount } = useAuthContext();
    const { canReview, canApprove, canApply } = useAdminAccess();
    const { environmentName } = useClientConfigs();
    const currentUserEmail = getUserAccount()?.username?.toLowerCase() ?? '';
    const [activeTab, setActiveTab] = useState<Tab>('pending');
    const [previewCrId, setPreviewCrId] = useState<number | null>(null);
    const [rejectCr, setRejectCr] = useState<ChangeRequest | null>(null);
    const [rejectReason, setRejectReason] = useState('');
    const [applyingId, setApplyingId] = useState<number | null>(null);
    const [applyConfirmCr, setApplyConfirmCr] = useState<ChangeRequest | null>(null);
    const [actionError, setActionError] = useState<{ crId: number; message: string } | null>(null);

    const { data, isLoading } = useChangeRequests(activeTab);
    const reviewMutation = useReviewChangeRequest();
    const approveMutation = useApproveChangeRequest();
    const rejectMutation = useRejectChangeRequest();
    const applyMutation = useApplyChangeRequest();

    const actionPayload = (comments?: string) => ({
        comments,
        actionBy: getUserName() ?? 'Agent',
        actionByEmail: getUserAccount()?.username ?? '',
    });

    const handleReview = async (cr: ChangeRequest) => {
        setActionError(null);
        try {
            await reviewMutation.mutateAsync({ id: cr.id, payload: actionPayload() });
        } catch (e: any) {
            setActionError({ crId: cr.id, message: extractActionError(e, 'Review failed. Please try again.') });
        }
    };

    const handleApprove = async (cr: ChangeRequest) => {
        setActionError(null);
        try {
            await approveMutation.mutateAsync({ id: cr.id, payload: actionPayload() });
        } catch (e: any) {
            setActionError({ crId: cr.id, message: extractActionError(e, 'Approval failed. Please try again.') });
        }
    };

    const handleReject = async () => {
        if (!rejectCr || !rejectReason) return;
        setActionError(null);
        try {
            await rejectMutation.mutateAsync({
                id: rejectCr.id,
                payload: actionPayload(rejectReason),
            });
            setRejectCr(null);
            setRejectReason('');
        } catch (e: any) {
            setActionError({ crId: rejectCr.id, message: extractActionError(e, 'Rejection failed. Please try again.') });
        }
    };

    const handleApply = async (cr: ChangeRequest) => {
        setApplyConfirmCr(null);
        setActionError(null);
        setApplyingId(cr.id);
        try {
            await applyMutation.mutateAsync({ id: cr.id, payload: actionPayload() });
        } catch (e: any) {
            setActionError({ crId: cr.id, message: extractActionError(e, 'Apply failed — please try again or check that the backend service is running.') });
        } finally {
            setApplyingId(null);
        }
    };

    const activeConfig = TAB_CONFIG.find(t => t.key === activeTab)!;

    const renderActions = (cr: ChangeRequest) => {
        const isSubmitter = !!currentUserEmail && cr.requestedByEmail?.toLowerCase() === currentUserEmail;

        if (activeTab === 'pending' && canReview) {
            if (isSubmitter) {
                return <SodNote text="You submitted this request" title="You submitted this request and cannot review it (segregation of duties)." />;
            }
            return (
                <>
                    <button type="button" className="admin-btn admin-btn-sm admin-btn-secondary" onClick={() => setRejectCr(cr)}>
                        Reject
                    </button>
                    <button type="button" className="admin-btn admin-btn-sm admin-btn-primary"
                        onClick={() => handleReview(cr)} disabled={reviewMutation.isPending}>
                        Mark as Reviewed
                    </button>
                </>
            );
        }

        if (activeTab === 'reviewed' && canApprove) {
            const isReviewer = !!currentUserEmail && cr.reviewedByEmail?.toLowerCase() === currentUserEmail;
            if (isReviewer || isSubmitter) {
                return isReviewer
                    ? <SodNote text="You reviewed this request" title="You reviewed this request and cannot approve it (segregation of duties)." />
                    : <SodNote text="You submitted this request" title="You submitted this request and cannot approve it." />;
            }
            return (
                <>
                    <button type="button" className="admin-btn admin-btn-sm admin-btn-secondary" onClick={() => setRejectCr(cr)}>
                        Reject
                    </button>
                    <button type="button" className="admin-btn admin-btn-sm admin-btn-primary"
                        onClick={() => handleApprove(cr)} disabled={approveMutation.isPending}>
                        Approve
                    </button>
                </>
            );
        }

        if (activeTab === 'approved' && canApply) {
            const isApplying = applyingId === cr.id;
            return (
                <button type="button" className="admin-btn admin-btn-sm admin-btn-primary"
                    onClick={() => setApplyConfirmCr(cr)}
                    disabled={isApplying || applyingId !== null}>
                    {isApplying
                        ? <><span className="admin-spin" /> Applying to Database…</>
                        : 'Apply to Database'}
                </button>
            );
        }

        return null;
    };

    return (
        <div className="admin-page">
            <div className="admin-page-header">
                <div>
                    <h1>Approval Queue</h1>
                    <p className="admin-page-subtitle">
                        Reviewers complete the first gate; Approvers complete the second. Reviewers then apply approved requests to the database.
                    </p>
                </div>
            </div>

            <div className="admin-tabs" role="tablist">
                {TAB_CONFIG.map(tab => (
                    <button key={tab.key} type="button" role="tab" aria-selected={activeTab === tab.key}
                        className={`admin-tab${activeTab === tab.key ? ' active' : ''}`}
                        onClick={() => { setActiveTab(tab.key); setPreviewCrId(null); }}>
                        {tab.label}
                    </button>
                ))}
            </div>

            {isLoading ? (
                <div className="admin-empty">
                    <span className="admin-spin admin-spin-lg" />
                    <p>Loading change requests…</p>
                </div>
            ) : !data?.data.length ? (
                <div className="admin-empty">{activeConfig.emptyMsg}</div>
            ) : (
                <div className="admin-cr-list">
                    {data.data.map(cr => {
                        const bulk = isBulk(cr);
                        const setup = readBulkSetup(cr.changeData);
                        const showPreview = previewCrId === cr.id;
                        return (
                            <article key={cr.id} className="admin-cr-card" aria-labelledby={`cr-${cr.id}-title`}>
                                <div className="admin-cr-body">
                                    <div className="admin-cr-head">
                                        <div>
                                            <p className="admin-cr-pretitle">Change request</p>
                                            <h3 id={`cr-${cr.id}-title`} className="admin-cr-id">CR-{cr.id}</h3>
                                            <div className="admin-cr-chips">
                                                <span className="admin-badge admin-badge-neutral">{friendlyTable(cr.targetTable)}</span>
                                                {bulk && (
                                                    <span className="admin-badge admin-badge-info">
                                                        Bulk · {cr.targetRecordIds.length} records
                                                    </span>
                                                )}
                                                {setup && (
                                                    <span className="admin-badge admin-badge-info">
                                                        Setup · {bulkSetupCounts(setup).rows} rows
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <div>
                                            <span className={`admin-tag ${activeConfig.tagClass}`}>{activeConfig.label}</span>
                                            <div className="admin-cr-date">{formatDate(cr.requestedAt)}</div>
                                        </div>
                                    </div>

                                    <dl className="admin-cr-details">
                                        <dt>Changes</dt>
                                        <dd><ChangesSummary cr={cr} /></dd>
                                        <dt>Submitted by</dt>
                                        <dd>{cr.requestedBy || '—'}</dd>
                                        {cr.reviewedBy && <><dt>Reviewed by</dt><dd>{cr.reviewedBy}</dd></>}
                                        {cr.approvedBy && <><dt>Approved by</dt><dd>{cr.approvedBy}</dd></>}
                                        {cr.comments && <><dt>Reason</dt><dd>{cr.comments}</dd></>}
                                    </dl>

                                    {cr.rejectionReason && (
                                        <div className="admin-alert admin-alert-error admin-alert-compact">
                                            <span className="admin-icon icon-alert" aria-hidden="true" />
                                            <div className="admin-alert-body">
                                                <strong>Rejection note: </strong>{cr.rejectionReason}
                                            </div>
                                        </div>
                                    )}

                                    {showPreview && <PreviewPanel cr={cr} />}

                                    {actionError?.crId === cr.id && (
                                        <div className="admin-alert admin-alert-error admin-alert-row admin-alert-compact" role="alert">
                                            <span className="admin-icon icon-alert" aria-hidden="true" />
                                            <div className="admin-alert-body">{actionError.message}</div>
                                            <button type="button" className="admin-btn admin-btn-sm admin-btn-ghost" onClick={() => setActionError(null)}>Dismiss</button>
                                        </div>
                                    )}
                                </div>

                                <div className="admin-cr-actions">
                                    <button type="button" className="admin-link-btn" aria-expanded={showPreview}
                                        onClick={() => setPreviewCrId(showPreview ? null : cr.id)}>
                                        {showPreview ? 'Hide preview' : 'Preview changes'}
                                    </button>
                                    <div className="admin-cr-actions-end">
                                        {renderActions(cr)}
                                    </div>
                                </div>
                            </article>
                        );
                    })}
                </div>
            )}

            {rejectCr && (
                <div className="admin-modal-backdrop">
                    <div className="admin-modal" role="dialog" aria-modal="true" aria-labelledby="reject-title">
                        <h3 id="reject-title">Reject Change Request</h3>
                        <p className="admin-muted">CR-{rejectCr.id} · {friendlyTable(rejectCr.targetTable)}</p>
                        <label className="admin-field-label" htmlFor="reject-reason">
                            Reason for rejection
                        </label>
                        <p className="admin-muted admin-stack-sm">This will be visible to the requester.</p>
                        <textarea
                            id="reject-reason"
                            className="admin-textarea"
                            value={rejectReason}
                            onChange={e => setRejectReason(e.target.value)}
                            placeholder="e.g. Incorrect carrier selected, please resubmit with the correct carrier"
                        />
                        <div className="admin-modal-actions">
                            <button type="button" className="admin-btn admin-btn-secondary" onClick={() => { setRejectCr(null); setRejectReason(''); }}>Cancel</button>
                            <button type="button" className="admin-btn admin-btn-primary"
                                onClick={handleReject}
                                disabled={!rejectReason.trim() || rejectMutation.isPending}>
                                {rejectMutation.isPending ? 'Rejecting…' : 'Confirm Rejection'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {applyConfirmCr && (
                <div className="admin-modal-backdrop">
                    <div className="admin-modal" role="dialog" aria-modal="true" aria-labelledby="apply-title">
                        <h3 id="apply-title">Apply to Database</h3>
                        <p className="admin-muted">CR-{applyConfirmCr.id} · {friendlyTable(applyConfirmCr.targetTable)}</p>
                        <div className="admin-alert">
                            <span className="admin-icon icon-alert" aria-hidden="true" />
                            <div className="admin-alert-body">
                                <strong>{applyEnvironmentWarning(environmentName)}</strong>
                                <div>{applyTargetText(applyConfirmCr)}</div>
                                {applyConfirmCr.comments && (
                                    <div className="admin-muted">Reason: {applyConfirmCr.comments}</div>
                                )}
                            </div>
                        </div>
                        <div className="admin-modal-actions">
                            <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setApplyConfirmCr(null)}>Cancel</button>
                            <button type="button" className="admin-btn admin-btn-primary" onClick={() => handleApply(applyConfirmCr)}>
                                Confirm &amp; Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
